/* Alliance Chemical concept product page (templates/product.ac-concept.json).
   Progressive enhancement for sections/ac-pdp-*.liquid; the page is complete
   without it. The server renders the size lineup already framed on the
   selected size, and the buy box is the theme's own (theme.js runs the
   variant pipeline, Add to cart is a native form post).

   Modules: stage (the lineup is a second size picker, synced both ways with
   the theme's Size radios), label inspector, molecule.
   Three.js scenes live in ac-pdp-gl.js and load only on capable devices,
   after load, the first interaction and an idle slot.
   No analytics events are emitted here. Quote links ([data-quote-pdp-link])
   get their size/pack/SKU/qty context and the quote_open event from the
   single handler in snippets/product-ai-summary.liquid, whose section is on
   this template and on product.json. */
(function () {
  "use strict";
  var root = typeof window !== "undefined" ? window : this;

  /* ------------------------------------------------------------ */
  /* Pure math (unit-tested in scripts/__tests__)                 */
  /* ------------------------------------------------------------ */

  function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
  }
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  var M = {
    clamp: clamp,
    lerp: lerp,

    /* Items stand edge to edge, separated by gap x the taller neighbour.
       x is each item's centre in inches from the left edge of the first. */
    layout: function (items, opts) {
      var gap = (opts && opts.gap) || 0.3;
      var out = [];
      var cursor = 0;
      items.forEach(function (it, i) {
        var w = it.h * it.a;
        if (i > 0) cursor += gap * Math.max(items[i - 1].h, it.h);
        out.push({ h: it.h, a: it.a, w: w, x: cursor + w / 2 });
        cursor += w;
      });
      out.total = cursor;
      return out;
    },

    /* Fraction of the stage height the focused item fills: the quart reads
       large, the tote leaves headroom. Log-like rational curve so Liquid can
       compute the same framing server-side. */
    focusFrac: function (h, hmin, hmax, lo, hi) {
      if (!(hmax > hmin)) return hi;
      var g = function (v) {
        return (v - hmin) / (v + 2 * hmin);
      };
      return lo + (hi - lo) * clamp(g(h) / g(hmax), 0, 1);
    },

    focusPpi: function (items, i, stage, opts) {
      var o = opts || {};
      var lo = o.lo != null ? o.lo : 0.4;
      var hi = o.hi != null ? o.hi : 0.72;
      var maxW = o.maxW || 0.85;
      var hmin = Infinity;
      var hmax = 0;
      items.forEach(function (it) {
        hmin = Math.min(hmin, it.h);
        hmax = Math.max(hmax, it.h);
      });
      var it = items[i];
      var ppi = (M.focusFrac(it.h, hmin, hmax, lo, hi) * stage.h) / it.h;
      return Math.min(ppi, (maxW * stage.w) / it.w);
    },

    /* Camera at fractional index f: x (inches) at the stage centre and ppi
       (pixels per inch). Between two items x moves linearly and ppi
       geometrically, which reads as a steady dolly. */
    camera: function (items, f, stage, opts) {
      var n = items.length;
      if (!n) return { x: 0, ppi: 1 };
      f = clamp(f, 0, n - 1);
      var i = Math.floor(f);
      var t = f - i;
      if (i >= n - 1) {
        i = n - 1;
        t = 0;
      }
      var a = { x: items[i].x, ppi: M.focusPpi(items, i, stage, opts) };
      if (t === 0) return a;
      var b = { x: items[i + 1].x, ppi: M.focusPpi(items, i + 1, stage, opts) };
      return M.mixCamera(a, b, t);
    },

    overview: function (items, stage) {
      var hmax = 0;
      items.forEach(function (it) {
        hmax = Math.max(hmax, it.h);
      });
      var ppi = Math.min(
        (0.8 * stage.h) / (hmax || 1),
        (0.92 * stage.w) / (items.total || 1),
      );
      return { x: (items.total || 0) / 2, ppi: ppi };
    },

    /* Horizontal correction (px, positive = content moves left) that keeps
       the first or last size from floating in the middle of an empty stage:
       an end of the lineup is pulled to the padding while the other end
       still has content past the opposite edge. Mirrors --shift in
       ac-pdp.css, which applies the same rule to the server-rendered frame. */
    edgeShift: function (items, cam, stage, padFrac) {
      var pad = (padFrac == null ? 0.09 : padFrac) * stage.w;
      var le = stage.w / 2 - cam.x * cam.ppi;
      var re = stage.w / 2 + ((items.total || 0) - cam.x) * cam.ppi;
      var left = Math.max(0, Math.min(le - pad, re - (stage.w - pad)));
      var right = Math.max(0, Math.min(stage.w - pad - re, pad - le));
      return left - right;
    },

    /* 1 for a container wholly inside the frame, 0 once more than a few
       percent of it is cut by an edge. A true-scale neighbour chopped by the stage edge
       reads as a rendering fault, so only whole containers are drawn
       (shiftPx as in edgeShift; pass 0 with an already-shifted camera). */
    visibility: function (it, cam, stage, shiftPx) {
      var c = stage.w / 2 + (it.x - cam.x) * cam.ppi - (shiftPx || 0);
      var half = (it.w * cam.ppi) / 2;
      if (!(half > 0)) return 0;
      var seen =
        (Math.min(stage.w, c + half) - Math.max(0, c - half)) / (2 * half);
      var t = clamp((seen - 0.93) / (0.99 - 0.93), 0, 1);
      return t * t * (3 - 2 * t);
    },

    mixCamera: function (a, b, t) {
      if (t <= 0) return { x: a.x, ppi: a.ppi };
      if (t >= 1) return { x: b.x, ppi: b.ppi };
      return {
        x: lerp(a.x, b.x, t),
        ppi: Math.exp(lerp(Math.log(a.ppi), Math.log(b.ppi), t)),
      };
    },

    fracIndex: function (left, cell, n) {
      if (!(cell > 0) || !(n > 0)) return 0;
      return clamp(left / cell, 0, n - 1);
    },

    /* Bounding box of non-white pixels (RGBA data), as fractions. */
    trimBox: function (data, w, h, threshold) {
      var x0 = w;
      var y0 = h;
      var x1 = -1;
      var y1 = -1;
      for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
          var i = (y * w + x) * 4;
          if (data[i + 3] < 16) continue;
          if (Math.min(data[i], data[i + 1], data[i + 2]) < threshold) {
            if (x < x0) x0 = x;
            if (x > x1) x1 = x;
            if (y < y0) y0 = y;
            if (y > y1) y1 = y;
          }
        }
      }
      if (x1 < 0) return null;
      return { x0: x0 / w, y0: y0 / h, x1: (x1 + 1) / w, y1: (y1 + 1) / h };
    },
  };

  root.__acPdpMath = M;
  if (typeof document === "undefined") return;
  if (root.__acPdpBooted) return;
  root.__acPdpBooted = true;

  /* ------------------------------------------------------------ */
  /* Shared helpers                                               */
  /* ------------------------------------------------------------ */

  /* Three.js r160 is served from the theme's own assets (Shopify's CDN,
     data-three-src on [data-ac-pdp]); jsDelivr is only a backup if that
     fails. Same file as the homepage hero. */
  var THREE_CDN_URL =
    "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js";
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var testMode = !!window.AC_PDP_TEST_MODE;

  function debug() {
    if (window.AC_PDP_DEBUG && window.console) {
      console.debug.apply(
        console,
        ["ac-pdp"].concat(Array.prototype.slice.call(arguments)),
      );
    }
  }

  /* Shopify's CDN minifier turns a literal import() into require(); building
     it inside a Function keeps it out of reach (same as ac-home-v2.js). */
  function loadModule(url) {
    return Promise.resolve().then(function () {
      return new Function("u", "return import(u)")(url);
    });
  }

  var scriptPromises = {};
  function loadScript(src) {
    if (!scriptPromises[src]) {
      scriptPromises[src] = new Promise(function (resolve, reject) {
        var s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = function () {
          resolve();
        };
        s.onerror = function () {
          reject(new Error("could not load " + src));
        };
        document.head.appendChild(s);
      });
    }
    return scriptPromises[src];
  }

  var threePromise = null;
  function loadGL(glSrc) {
    if (!threePromise) {
      var host = document.querySelector("[data-ac-pdp][data-three-src]");
      var ownThree = host && host.getAttribute("data-three-src");
      var three = ownThree
        ? loadModule(ownThree).catch(function (err) {
            debug("own three.js failed, trying the CDN", err);
            return loadModule(THREE_CDN_URL);
          })
        : loadModule(THREE_CDN_URL);
      threePromise = Promise.all([three, loadScript(glSrc)]).then(
        function (mods) {
          if (!window.AC_PDP_GL) throw new Error("ac-pdp-gl.js did not load");
          return mods[0];
        },
      );
    }
    return threePromise;
  }

  function afterLoad(fn) {
    if (document.readyState === "complete") fn();
    else window.addEventListener("load", fn, { once: true });
  }

  function idle(fn) {
    if ("requestIdleCallback" in window)
      window.requestIdleCallback(fn, { timeout: 2500 });
    else setTimeout(fn, 600);
  }

  /* Three.js is fetched only for visitors who touch, scroll, point or type;
     the server-rendered lineup is the same picture until then. */
  function onFirstInteraction(fn) {
    if (testMode) {
      fn();
      return function () {};
    }
    var events = [
      "pointermove",
      "pointerdown",
      "keydown",
      "wheel",
      "touchstart",
      "scroll",
    ];
    var opts = { passive: true, capture: true };
    var done = false;
    function off() {
      events.forEach(function (e) {
        window.removeEventListener(e, go, opts);
      });
    }
    function go() {
      if (done) return;
      done = true;
      off();
      fn();
    }
    events.forEach(function (e) {
      window.addEventListener(e, go, opts);
    });
    return function cancel() {
      done = true;
      off();
    };
  }

  function whenVisible(el, fn) {
    if (!("IntersectionObserver" in window)) return function () {};
    var io = new IntersectionObserver(
      function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        fn();
      },
      { threshold: 0.1 },
    );
    io.observe(el);
    return function () {
      io.disconnect();
    };
  }

  /* Gate for every WebGL scene on this page. Phones are included; the
     scenes themselves refuse software rendering and watch their frame cost. */
  function glAllowed() {
    return (
      !reduceMotion.matches &&
      !saveData &&
      "IntersectionObserver" in window &&
      !!window.WebGL2RenderingContext
    );
  }

  /* The one gate every WebGL scene waits behind: element on screen, page
     loaded, first interaction, then an idle slot. Returns a cancel. */
  function whenGLReady(el, fn) {
    var cancels = [];
    var cancelled = false;
    cancels.push(
      whenVisible(el, function () {
        afterLoad(function () {
          if (cancelled) return;
          cancels.push(
            onFirstInteraction(function () {
              idle(function () {
                if (!cancelled && !reduceMotion.matches) fn();
              });
            }),
          );
        });
      }),
    );
    return function () {
      cancelled = true;
      cancels.forEach(function (c) {
        c();
      });
    };
  }

  function easeInOut(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function num(el, name, fallback) {
    var v = parseFloat(el.getAttribute(name));
    return isFinite(v) ? v : fallback;
  }

  function cssEscape(s) {
    return window.CSS && CSS.escape
      ? CSS.escape(s)
      : String(s).replace(/["\\]/g, "\\$&");
  }

  /* ------------------------------------------------------------ */
  /* Stage: the size lineup                                        */
  /* ------------------------------------------------------------ */

  function initStage(stage) {
    var view = stage.querySelector(".ac-stage__view");
    var scene = stage.querySelector("[data-stage-scene]");
    var rail = stage.querySelector("[data-stage-rail]");
    var itemEls = Array.prototype.slice.call(
      stage.querySelectorAll("[data-stage-item]"),
    );
    if (!view || !scene || !rail || itemEls.length < 2) return null;

    var wrapper = stage.closest("[data-product-wrapper]") || document;

    /* Browsers without container-query units (Safari < 16, Chrome < 105):
       ac-pdp.css shows only the selected size as a plain photo. Keep that
       photo in step with the Size radios and stop there. */
    if (!(window.CSS && CSS.supports && CSS.supports("height", "1cqh"))) {
      var onPick = function (e) {
        var t = e.target;
        if (!t || !t.matches) return;
        if (!t.matches('[data-opt-index="option1"] input[type="radio"]'))
          return;
        itemEls.forEach(function (el) {
          el.classList.toggle(
            "is-current",
            el.getAttribute("data-size") === t.value,
          );
        });
      };
      wrapper.addEventListener("change", onPick);
      return {
        destroy: function () {
          wrapper.removeEventListener("change", onPick);
        },
      };
    }
    var tagEls = Array.prototype.slice.call(
      stage.querySelectorAll("[data-stage-tag]"),
    );
    var dim = stage.querySelector("[data-stage-dim]");
    var dimLabel = stage.querySelector("[data-stage-dim-label]");
    var sizeOut = stage.querySelector("[data-stage-size]");
    var heightOut = stage.querySelector("[data-stage-height]");
    var liveOut = stage.querySelector("[data-stage-live]");
    var heightTemplate =
      (heightOut && heightOut.getAttribute("data-template")) || "";
    var prevBtn = stage.querySelector("[data-stage-prev]");
    var nextBtn = stage.querySelector("[data-stage-next]");
    var allBtn = stage.querySelector("[data-stage-all]");
    var inspectBtn = stage.querySelector("[data-stage-inspect]");
    var dialog = stage.querySelector("[data-stage-dialog]");
    var canvas = stage.querySelector("[data-stage-canvas]");
    var cleanups = [];
    var dead = false;

    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      cleanups.push(function () {
        el.removeEventListener(type, fn, opts);
      });
    }

    var data = itemEls.map(function (el) {
      return {
        el: el,
        size: el.getAttribute("data-size"),
        label: el.getAttribute("data-label") || el.getAttribute("data-size"),
        h: num(el, "data-h", 12),
        a: num(el, "data-a", 0.6),
        known: el.getAttribute("data-known") === "true",
        low: el.getAttribute("data-low"),
        tex: el.getAttribute("data-tex"),
        full: el.getAttribute("data-full"),
        alt: el.getAttribute("data-alt") || "",
      };
    });
    var n = data.length;
    var items = M.layout(data, { gap: 0.3 });
    var dims = { w: view.clientWidth || 1, h: view.clientHeight || 1 };
    var floorFrac = 0.15; /* --floor: 15cqh in ac-pdp.css */

    var state = {
      f: Math.max(0, Math.min(n - 1, num(stage, "data-selected", 0))),
      blend: 1 /* 1 = focused on a size, 0 = all sizes */,
      blendFrom: 1,
      blendTo: 1,
      blendStart: 0,
      blendDur: 0,
      intro: 0 /* extra pull-back while the WebGL scene fades in */,
      selected: -1,
      target: null,
    };
    var gl = null;
    var raf = 0;
    var fromStage = false;
    var holding = false; /* a finger or mouse is still on the rail */

    function cellWidth() {
      var cell = rail.querySelector("[data-stage-cell]");
      return cell ? cell.getBoundingClientRect().width : dims.w / 2;
    }

    function currentCamera() {
      var focus = M.camera(items, state.f, dims);
      var cam = focus;
      if (state.blend < 1) {
        cam = M.mixCamera(
          M.overview(items, dims),
          focus,
          easeInOut(state.blend),
        );
      }
      if (state.intro > 0) {
        cam = { x: cam.x, ppi: cam.ppi * (1 - 0.07 * easeInOut(state.intro)) };
      }
      return cam;
    }

    /* The camera as drawn: CSS applies the edge shift itself (--shift), so
       the WebGL scene and hit-testing use this shifted x. */
    function viewCamera(cam) {
      var shift = M.edgeShift(items, cam, dims, 0.09);
      return { x: cam.x + shift / cam.ppi, ppi: cam.ppi };
    }

    function render(now) {
      raf = 0;
      if (dead) return;
      var animating = false;
      if (state.blendDur > 0) {
        var p = Math.min(1, (now - state.blendStart) / state.blendDur);
        state.blend = lerp(state.blendFrom, state.blendTo, p);
        if (p < 1) animating = true;
        else state.blendDur = 0;
      }
      if (state.introStart) {
        var q = Math.min(1, (now - state.introStart) / 900);
        state.intro = 1 - q;
        if (q < 1) animating = true;
        else {
          state.intro = 0;
          state.introStart = 0;
        }
      }
      var cam = currentCamera();
      var fppi = cam.ppi / dims.h;
      scene.style.setProperty("--cx", cam.x.toFixed(3));
      scene.style.setProperty("--fppi", fppi.toFixed(6));
      var view3d = viewCamera(cam);
      var vis = items.map(function (it) {
        return M.visibility(it, view3d, dims, 0);
      });
      vis.forEach(function (v, i) {
        var val = v.toFixed(2);
        if (itemEls[i].style.getPropertyValue("--vis") !== val) {
          itemEls[i].style.setProperty("--vis", val);
          if (tagEls[i]) tagEls[i].style.setProperty("--vis", val);
        }
      });

      var k = Math.round(state.f);
      var settled = Math.abs(state.f - k) < 0.08 && state.blend > 0.97;
      if (dim) {
        var it = items[k];
        var show = settled && data[k].known;
        /* Beside the container's right edge, or its left when the label
           would run past the frame (the last size sits near the edge). */
        var rightPx = dims.w / 2 + (it.x + it.w / 2 - view3d.x) * view3d.ppi;
        var flip = dims.w - rightPx < 76;
        dim.classList.toggle("is-left", flip);
        dim.style.setProperty(
          "--dx",
          (flip ? it.x - it.w / 2 : it.x + it.w / 2).toFixed(3),
        );
        dim.style.setProperty("--dh", it.h.toFixed(3));
        dim.classList.toggle("is-on", show);
      }
      stage.setAttribute("data-mode", state.blend < 0.5 ? "all" : "focus");
      tagEls.forEach(function (tag, i) {
        tag.classList.toggle("is-current", i === k);
      });
      if (k !== state.captionIndex) {
        state.captionIndex = k;
        itemEls.forEach(function (el, i) {
          el.classList.toggle("is-current", i === k);
        });
        if (sizeOut) sizeOut.textContent = stack ? stack.label : data[k].label;
        if (heightOut) {
          heightOut.textContent = data[k].known
            ? heightOut
                .getAttribute("data-template")
                .replace("[in]", String(Math.round(data[k].h)))
            : "";
        }
        if (dimLabel)
          dimLabel.textContent = data[k].known
            ? dimLabel
                .getAttribute("data-template")
                .replace("[in]", String(Math.round(data[k].h)))
            : "";
        if (prevBtn)
          prevBtn.setAttribute("aria-disabled", k <= 0 ? "true" : "false");
        if (nextBtn)
          nextBtn.setAttribute("aria-disabled", k >= n - 1 ? "true" : "false");
      }
      if (gl) gl.render(view3d, state.f, floorFrac, state.blend, vis);
      if (animating) raf = requestAnimationFrame(render);
    }

    function requestRender() {
      if (!raf && !dead) raf = requestAnimationFrame(render);
    }

    /* Scroll position of the transparent rail drives the camera. */
    function scrollToIndex(i, smooth) {
      i = clamp(i, 0, n - 1);
      state.target = i; /* where a scroll we started is heading */
      var left = i * cellWidth();
      if (smooth && !reduceMotion.matches && rail.scrollTo) {
        rail.scrollTo({ left: left, behavior: "smooth" });
      } else {
        rail.scrollLeft = left;
        state.f = i;
        requestRender();
      }
    }

    var settleTimer = 0;
    function onScroll() {
      state.f = M.fracIndex(rail.scrollLeft, cellWidth(), n);
      requestRender();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, "onscrollend" in window ? 400 : 140);
    }
    /* Select only once the rail is at rest and released: selecting runs the
       theme's whole variant pipeline and rewrites the URL. */
    function nearest() {
      return Math.round(M.fracIndex(rail.scrollLeft, cellWidth(), n));
    }
    function onSettle() {
      clearTimeout(settleTimer);
      if (holding) return;
      state.target = null;
      selectSize(nearest(), true);
    }
    /* Buying must never lag the picture. A buy button pressed while the
       lineup is still moving first commits the size on screen (or the size a
       radio click is scrolling to), synchronously: theme.js updates the
       form's variant id inside this call, before the form data is read. */
    function commit() {
      clearTimeout(settleTimer);
      holding = false;
      var k = state.target != null ? state.target : nearest();
      selectSize(k, true);
    }
    var cartForm = wrapper.querySelector('form[data-type="add-to-cart-form"]');
    on(cartForm, "submit", commit, true);
    on(rail, "scroll", onScroll, { passive: true });
    on(rail, "scrollend", onSettle);
    function hold() {
      holding = true;
      state.touched = true;
      state.target = null; /* the visitor took over the scroll */
      if (state.blend < 1) animateBlend(1); /* swiping leaves "All sizes" */
    }
    function release() {
      if (!holding) return;
      holding = false;
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, 160);
    }
    on(rail, "touchstart", hold, { passive: true });
    on(rail, "touchend", release, { passive: true });
    on(rail, "touchcancel", release, { passive: true });
    on(rail, "pointerdown", hold);
    on(window, "pointerup", release);
    on(window, "pointercancel", release);
    on(window, "blur", release);
    /* The rail is aria-hidden; a mouse press must not move focus onto it. */
    on(rail, "mousedown", function (e) {
      e.preventDefault();
    });

    /* Stage -> theme picker: click the matching Size radio so theme.js runs
       its own variant pipeline (price, availability, URL, sticky bar). */
    function sizeRadio(size) {
      return wrapper.querySelector(
        '[data-product-loop-variants][data-opt-index="option1"] input[type="radio"][value="' +
          cssEscape(size) +
          '"]',
      );
    }
    function checkedSizeIndex() {
      var list = wrapper.querySelector(
        '[data-product-loop-variants][data-opt-index="option1"]',
      );
      var checked = list && list.querySelector('input[type="radio"]:checked');
      if (!checked) return -1;
      for (var i = 0; i < n; i++) if (data[i].size === checked.value) return i;
      return -1;
    }
    function announce(k) {
      if (!liveOut) return;
      var h = data[k].known
        ? " " + heightTemplate.replace("[in]", String(Math.round(data[k].h)))
        : "";
      liveOut.textContent = data[k].label + "." + h;
    }
    function selectSize(k, byStage) {
      if (k === state.selected) return;
      state.selected = k;
      if (byStage) announce(k);
      var radio = sizeRadio(data[k].size);
      if (radio && !radio.checked) {
        fromStage = true;
        radio.click();
        fromStage = false;
      }
    }

    /* Theme picker -> stage. */
    on(wrapper, "change", function (e) {
      var t = e.target;
      if (fromStage || !t || !t.matches) return;
      if (!t.matches('[data-opt-index="option1"] input[type="radio"]')) return;
      var i = -1;
      for (var j = 0; j < n; j++) if (data[j].size === t.value) i = j;
      if (i < 0) return;
      state.selected = i;
      if (state.blend < 1) animateBlend(1);
      scrollToIndex(i, true);
    });

    /* Tap a container to bring it into focus. The rail sits on top of the
       picture, so hit-test against the camera instead of the DOM. */
    on(rail, "click", function (e) {
      if (dragMoved) return;
      var r = view.getBoundingClientRect();
      var px = e.clientX - r.left - dims.w / 2;
      var cam = viewCamera(currentCamera());
      var best = -1;
      var bestD = Infinity;
      items.forEach(function (it, i) {
        var cx = (it.x - cam.x) * cam.ppi;
        var half = Math.max((it.w * cam.ppi) / 2, 22);
        var d = Math.abs(px - cx);
        if (d <= half && d < bestD) {
          best = i;
          bestD = d;
        }
      });
      if (best < 0) return;
      if (state.blend < 1) animateBlend(1);
      scrollToIndex(best, true);
      if (Math.round(state.f) === best) selectSize(best, true);
    });

    /* Mouse drag scrolls the rail (touch and trackpads scroll natively). */
    var drag = null;
    var dragMoved = false;
    on(rail, "pointerdown", function (e) {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      drag = { x: e.clientX, left: rail.scrollLeft };
      dragMoved = false;
      rail.classList.add("is-dragging");
    });
    on(window, "pointermove", function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (Math.abs(dx) > 4) dragMoved = true;
      rail.scrollLeft = drag.left - dx;
    });
    function endDrag() {
      if (!drag) return;
      drag = null;
      rail.classList.remove("is-dragging");
      var k = Math.round(M.fracIndex(rail.scrollLeft, cellWidth(), n));
      scrollToIndex(k, true);
      setTimeout(function () {
        dragMoved = false;
      }, 0);
    }
    on(window, "pointerup", endDrag);
    on(window, "pointercancel", endDrag);

    /* aria-disabled, not disabled: a disabled button drops keyboard focus
       to <body> when the lineup reaches an end. */
    function step(dir) {
      var k = Math.round(state.f) + dir;
      if (k < 0 || k > n - 1) return;
      if (state.blend < 1) animateBlend(1);
      scrollToIndex(k, true);
    }
    on(prevBtn, "click", function () {
      step(-1);
    });
    on(nextBtn, "click", function () {
      step(1);
    });

    function animateBlend(to) {
      if (reduceMotion.matches) {
        state.blend = to;
        state.blendDur = 0;
      } else {
        state.blendFrom = state.blend;
        state.blendTo = to;
        state.blendStart = performance.now();
        state.blendDur = 650;
      }
      if (allBtn)
        allBtn.setAttribute("aria-pressed", to < 1 ? "true" : "false");
      requestRender();
    }
    on(allBtn, "click", function () {
      if (stack) {
        leavePhoto();
        animateBlend(0);
        return;
      }
      animateBlend(state.blend >= 0.5 ? 0 : 1);
    });

    /* Label inspector: the selected size's photo, large. */
    on(inspectBtn, "click", function () {
      if (!dialog || !dialog.showModal) return;
      var d = stack || data[Math.round(state.f)];
      var img = dialog.querySelector("img");
      if (img && d.full) {
        img.src = d.full;
        img.alt = d.alt;
      }
      var cap = dialog.querySelector("[data-stage-dialog-caption]");
      if (cap) cap.textContent = d.label;
      dialog.showModal();
    });
    on(dialog, "click", function (e) {
      if (
        e.target === dialog ||
        (e.target.closest && e.target.closest("[data-stage-dialog-close]"))
      )
        dialog.close();
    });

    /* Resize: keep the same size centred. */
    function onResize() {
      var w = view.clientWidth;
      var h = view.clientHeight;
      if (!w || !h) return;
      dims = { w: w, h: h };
      var k = Math.round(state.f);
      rail.scrollLeft = k * cellWidth();
      state.f = k;
      requestRender();
      if (gl) gl.resize(dims);
    }
    var ro = "ResizeObserver" in window ? new ResizeObserver(onResize) : null;
    if (ro) {
      ro.observe(view);
      cleanups.push(function () {
        ro.disconnect();
      });
    }

    /* Trim each render's white margin so containers stand on the floor at
       their true height. Tiny copies (96 px); CORS is open on Shopify's CDN. */
    function measure(d, i) {
      return new Promise(function (resolve) {
        if (!d.low) return resolve();
        var img = new Image();
        img.crossOrigin = "anonymous";
        img.decoding = "async";
        img.onload = function () {
          try {
            var w = img.naturalWidth;
            var h = img.naturalHeight;
            var c = document.createElement("canvas");
            c.width = w;
            c.height = h;
            var ctx = c.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(img, 0, 0);
            var box = M.trimBox(ctx.getImageData(0, 0, w, h).data, w, h, 238);
            if (box && box.y1 - box.y0 > 0.2) {
              d.ar = w / h;
              d.a = ((box.x1 - box.x0) * d.ar) / (box.y1 - box.y0);
              var el = itemEls[i];
              el.style.setProperty("--by0", box.y0.toFixed(4));
              el.style.setProperty("--by1", box.y1.toFixed(4));
              el.style.setProperty("--bcx", ((box.x0 + box.x1) / 2).toFixed(4));
              el.style.setProperty("--a", d.a.toFixed(4));
            }
          } catch (err) {
            debug("trim failed", d.size, err);
          }
          resolve();
        };
        img.onerror = function () {
          resolve();
        };
        img.src = d.low;
      });
    }
    function relayout() {
      items = M.layout(data, { gap: 0.3 });
      scene.style.setProperty("--total", items.total.toFixed(3));
      items.forEach(function (it, i) {
        itemEls[i].style.setProperty("--x", it.x.toFixed(3));
        if (tagEls[i]) tagEls[i].style.setProperty("--x", it.x.toFixed(3));
      });
      if (gl) gl.setItems(items, data);
      requestRender();
    }
    idle(function () {
      if (dead) return;
      Promise.all(data.map(measure)).then(function () {
        if (dead) return;
        relayout();
      });
    });

    /* Theme variant -> stage photo. Each size stands in the lineup as its
       first variant's photo; picking another pack of that size (option 2:
       "4 Quarts", "4 Gallon Case", a pallet) shows that pack's photo. */
    var variants = [];
    var variantJson = wrapper.querySelector(
      '[type="application/json"][data-name="main-product"]',
    );
    try {
      variants = JSON.parse((variantJson && variantJson.textContent) || "[]");
    } catch (err) {
      debug("stage variants unreadable", err);
    }
    var swaps = 0;
    var photo = stage.querySelector("[data-stage-photo]");
    var stack = null; /* the pallet photo on show instead of the lineup */
    function cdn(src, w) {
      return src + (src.indexOf("?") > -1 ? "&" : "?") + "width=" + w;
    }
    function srcset(src, widths) {
      return widths
        .map(function (w) {
          return cdn(src, w) + " " + w + "w";
        })
        .join(", ");
    }
    /* Same test as stack_photo in ac-pdp-stage.liquid. */
    function isStack(v) {
      return /pallet| x 36/i.test((v && v.option2) || "");
    }
    function leavePhoto() {
      if (!stack) return;
      stack = null;
      stage.classList.remove("is-photo");
      state.captionIndex = -1; /* re-draw the size caption */
      requestRender();
    }
    function showVariantPhoto(v) {
      var src = v && v.featured_image && v.featured_image.src;
      if (!src || !isStack(v) || !photo) leavePhoto();
      if (!src) return;
      if (isStack(v) && photo) {
        stack = {
          full: cdn(src, 1600),
          alt: v.featured_image.alt || "",
          label: v.option2,
        };
        photo.srcset = srcset(src, [480, 640, 800, 960, 1200]);
        photo.sizes = "(min-width: 1024px) 50vw, 100vw";
        photo.src = cdn(src, 1200);
        photo.alt = stack.alt;
        stage.classList.add("is-photo");
        if (sizeOut) sizeOut.textContent = stack.label;
        return;
      }
      var i = -1;
      for (var j = 0; j < n; j++) if (data[j].size === v.option1) i = j;
      if (i < 0) return;
      var d = data[i];
      var tex = cdn(src, 1024);
      if (d.tex === tex) return;
      d.low = cdn(src, 96);
      d.tex = tex;
      d.full = cdn(src, 1600);
      if (v.featured_image.alt) d.alt = v.featured_image.alt;
      var img = itemEls[i].querySelector("img");
      if (img) {
        img.srcset = srcset(src, [240, 360, 480, 640, 800, 960, 1200]);
        img.src = cdn(src, 1200);
        img.alt = d.alt;
      }
      var token = (d.swap = ++swaps);
      measure(d, i).then(function () {
        if (dead || d.swap !== token) return;
        relayout();
        if (gl)
          gl.setTexture(i, d.tex).catch(function (err) {
            debug("stage texture swap failed", d.size, err);
          });
      });
    }
    function variantById(id) {
      return variants.find(function (x) {
        return String(x.id) === String(id);
      });
    }
    on(wrapper, "change", function (e) {
      var t = e.target;
      if (!t || t.name !== "id") return;
      showVariantPhoto(variantById(t.value));
    });
    /* The Liquid already drew a pallet page as .is-photo; adopt it. */
    if (stage.classList.contains("is-photo")) {
      var idInput = wrapper.querySelector('[name="id"]');
      var first = idInput && variantById(idInput.value);
      if (first && isStack(first)) showVariantPhoto(first);
      else stage.classList.remove("is-photo");
    }

    /* WebGL: reflections, lens blur on the sizes out of focus, haze. Fades
       in over the DOM lineup at identical framing, with one gentle settle. */
    var glSrc = stage.getAttribute("data-gl-src");
    if (canvas && glSrc && glAllowed()) {
      cleanups.push(
        whenGLReady(view, function () {
          if (dead) return;
          loadGL(glSrc)
            .then(function (THREE) {
              if (dead) return;
              gl = window.AC_PDP_GL.stage(THREE, {
                canvas: canvas,
                items: items,
                data: data,
                dims: dims,
                test: testMode,
                onLive: function () {
                  stage.classList.add("is-gl");
                  if (!reduceMotion.matches && !state.touched) {
                    state.introStart = performance.now();
                  }
                  requestRender();
                },
                onFail: function (why) {
                  debug("stage gl off:", why);
                  gl = null;
                  stage.classList.remove("is-gl");
                },
              });
              requestRender();
            })
            .catch(function (err) {
              gl = null;
              debug("stage gl unavailable", err);
            });
        }),
      );
    }
    function onMotionPref() {
      if (reduceMotion.matches && gl) {
        gl.destroy();
        gl = null;
        stage.classList.remove("is-gl");
      }
    }
    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener("change", onMotionPref);
      cleanups.push(function () {
        reduceMotion.removeEventListener("change", onMotionPref);
      });
    }

    /* Boot: line the rail up with the server-rendered framing. */
    var initial = checkedSizeIndex();
    if (initial < 0) initial = Math.round(state.f);
    state.selected = initial;
    state.f = initial;
    stage.classList.add("is-js");
    rail.scrollLeft = initial * cellWidth();
    requestRender();

    return {
      destroy: function () {
        dead = true;
        if (raf) cancelAnimationFrame(raf);
        clearTimeout(settleTimer);
        cleanups.forEach(function (fn) {
          fn();
        });
        if (gl) gl.destroy();
        gl = null;
        stage.classList.remove("is-js", "is-gl");
      },
    };
  }

  /* ------------------------------------------------------------ */
  /* Molecule (identity band)                                     */
  /* ------------------------------------------------------------ */

  function initMolecule(box) {
    var canvas = box.querySelector("canvas");
    var glSrc = box.getAttribute("data-gl-src");
    var dataSrc = box.getAttribute("data-molecules-src");
    var key = box.getAttribute("data-molecule");
    var dead = false;
    var scene = null;
    var cleanups = [];
    if (!canvas || !glSrc || !dataSrc || !key || !glAllowed()) return null;
    cleanups.push(
      whenGLReady(box, function () {
        if (dead) return;
        Promise.all([loadGL(glSrc), loadScript(dataSrc)])
          .then(function (mods) {
            if (dead || !window.AC_PDP_MOLECULES) return;
            scene = window.AC_PDP_GL.molecule(mods[0], {
              box: box,
              canvas: canvas,
              data: window.AC_PDP_MOLECULES,
              key: key,
              test: testMode,
              onFail: function () {
                scene = null;
              },
            });
          })
          .catch(function (err) {
            scene = null;
            debug("molecule unavailable", err);
          });
      }),
    );
    return {
      destroy: function () {
        dead = true;
        cleanups.forEach(function (fn) {
          fn();
        });
        if (scene) scene.destroy();
        scene = null;
      },
    };
  }

  /* ------------------------------------------------------------ */
  /* Buy summary. The quick-buy row and the theme's scroll bar must */
  /* say what the cart will get: pack, quantity, subscription and   */
  /* its price, sold-out state. Also guards the cart form against   */
  /* double submits (a triple tap used to add three).               */
  /* ------------------------------------------------------------ */

  function initQuick(box) {
    var wrapper = box.closest("[data-product-wrapper]");
    var section = wrapper.closest(".shopify-section") || document;
    var form = wrapper.querySelector('form[data-type="add-to-cart-form"]');
    var btn = box.querySelector("[data-ac-quick-btn]");
    var pack = box.querySelector("[data-ac-quick-pack]");
    var note = box.querySelector("[data-ac-quick-note]");
    var priceOut = box.querySelector("[data-regular-price]");
    var json = wrapper.querySelector(
      '[type="application/json"][data-name="main-product"]',
    );
    if (!btn || !form || !json) return null;
    var variants = [];
    try {
      variants = JSON.parse(json.textContent || "[]");
    } catch (e) {
      debug("quick buy: variant json", e);
    }
    var single = variants.length < 2;
    /* The pack line under the buy row: one line per size, from the
       section (sizes with a single pack have none). */
    var cue = wrapper.querySelector("[data-ac-packs]");
    var cueText = cue && cue.querySelector("[data-ac-packs-text]");
    var cueMap = {};
    var cueSrc = wrapper.querySelector("[data-ac-packs-map]");
    if (cueSrc) {
      try {
        cueMap = JSON.parse("{" + (cueSrc.textContent || "") + "}");
      } catch (e) {
        debug("quick buy: pack map json", e);
      }
    }
    var subLabel = box.getAttribute("data-label-subscribe") || "";
    var everyLabel = box.getAttribute("data-label-every") || "";
    var cleanups = [];
    function on(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      cleanups.push(function () {
        el.removeEventListener(type, fn, opts);
      });
    }

    /* The shop format is a top-level `const moneyFormat` in
       snippets/theme-variables.liquid: a global binding but NOT a window
       property, so `window.moneyFormat` was always undefined and every
       price fell to the bare fallback ("$3982.47"; the pack ladder and
       theme.js say "$3,982.47"). */
    function money(cents) {
      var fmt =
        (typeof moneyFormat !== "undefined" && moneyFormat) ||
        (window.Shopify && Shopify.money_format) ||
        window.moneyFormat;
      if (window.Shopify && typeof Shopify.formatMoney === "function" && fmt) {
        try {
          return Shopify.formatMoney(cents, fmt);
        } catch (e) {
          debug("quick buy: formatMoney", e);
        }
      }
      var parts = (cents / 100).toFixed(2).split(".");
      return (
        "$" + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + parts[1]
      );
    }
    function variant() {
      var input = form.querySelector('[name="id"]');
      var id = input ? String(input.value) : "";
      return variants.find(function (x) {
        return String(x.id) === id;
      });
    }
    function quantity() {
      var q = wrapper.querySelector("[data-quantity-input]");
      var v = q ? parseInt(q.value, 10) : 1;
      return v > 1 ? v : 1;
    }
    /* The chosen subscription, read from the theme's purchase widget. */
    function plan() {
      var input = form.querySelector('input[name="selling_plan"]');
      if (!input || !input.value) return null;
      var row = wrapper.querySelector(
        ".ac-pdp__buy .product-cstm-input:not(.hide_widget) .product-cstm-input-inner.current",
      );
      var select = row && row.querySelector("select.form-select");
      var opt = select && select.selectedOptions && select.selectedOptions[0];
      if (!opt) return { price: "", every: "" };
      return {
        price: opt.getAttribute("data-price") || "",
        every: (opt.textContent || "").trim(),
      };
    }

    var barNote = null;
    function barNoteEl() {
      if (barNote && barNote.isConnected) return barNote;
      var info = section.querySelector(
        "[data-sticky-products] .product-add-to-cart-sticky-info",
      );
      if (!info) return null;
      barNote = document.createElement("p");
      barNote.className = "ac-pdp-bar-note";
      info.appendChild(barNote);
      return barNote;
    }

    function sync() {
      var v = variant();
      if (!v) return;
      var q = quantity();
      var sub = plan();
      var parts = [];
      if (!single) parts.push(v.options[v.options.length - 1]);
      if (pack) pack.textContent = parts.join("");
      if (cueText) {
        var line = cueMap[v.options[0]] || "";
        if (line && cueText.textContent !== line) cueText.textContent = line;
        cue.hidden = !line;
      }
      var extras = [];
      if (q > 1) extras.push("\u00d7 " + q);
      if (sub)
        extras.push(
          subLabel + (sub.every ? ", " + everyLabel + " " + sub.every : ""),
        );
      if (note) {
        note.textContent = extras.join(" \u00b7 ");
        note.hidden = extras.length === 0;
      }
      var shown = sub && sub.price ? sub.price : money(v.price);
      if (priceOut) priceOut.textContent = shown;
      /* The theme's scroll bar: same price, and a short note of what else
         the button will add. */
      var barPrice = section.querySelector(
        "[data-sticky-products] [data-regular-price]",
      );
      if (barPrice) barPrice.textContent = shown;
      var bar = barNoteEl();
      if (bar) {
        bar.textContent = extras.join(" \u00b7 ");
        bar.hidden = extras.length === 0;
      }
      /* Sold-out state for every buy button. theme.js only refreshes
         [data-add-to-cart] buttons, which this store's page-cart setup does
         not render, so the main button would keep its server state. */
      var label = btn.getAttribute(
        v.available ? "data-label-add" : "data-label-sold",
      );
      btn.disabled = !v.available;
      btn.textContent = label;
      var main = document.getElementById("AddToCart-" + form.id);
      if (main) {
        main.disabled = !v.available;
        var span = main.querySelector("span");
        if (span) span.textContent = label;
      }
    }

    /* Anything that can change the summary: variant, purchase type, interval,
       quantity field and its steppers. Read state after the theme's own
       handlers have run. */
    var queued = false;
    function queue() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        sync();
      });
    }
    on(wrapper, "change", queue);
    on(wrapper, "input", queue);
    on(wrapper, "click", queue);
    on(section, "click", queue);

    /* One submit per page view. Cleared when the page is shown again
       (back/forward cache) or if the navigation never happens. */
    var submitting = 0;
    function onSubmit(e) {
      if (submitting) {
        e.preventDefault();
        return;
      }
      submitting = setTimeout(function () {
        submitting = 0;
        box.classList.remove("is-busy");
      }, 8000);
      box.classList.add("is-busy");
    }
    on(form, "submit", onSubmit);

    /* The pack line jumps to the pack list and puts focus on the picked
       pack, so the next tap or arrow key is a pack choice. */
    on(cue, "click", function () {
      var list = wrapper.querySelector(
        '.ac-pdp__buy [data-product-loop-variants][data-opt-index="option2"]',
      );
      if (!list) return;
      var reduce =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      (list.closest(".product-selector-option") || list).scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
      var picked = list.querySelector("input:checked");
      if (picked) picked.focus({ preventScroll: true });
    });
    on(window, "pageshow", function () {
      clearTimeout(submitting);
      submitting = 0;
      box.classList.remove("is-busy");
      sync();
    });

    sync();
    return {
      destroy: function () {
        clearTimeout(submitting);
        cleanups.forEach(function (fn) {
          fn();
        });
        if (barNote && barNote.parentNode)
          barNote.parentNode.removeChild(barNote);
      },
    };
  }

  /* ------------------------------------------------------------ */
  /* Products without a size lineup: the photo follows the variant */
  /* (the theme gallery that used to do this is not on this page). */
  /* ------------------------------------------------------------ */

  function initSingle(stage) {
    var wrapper = stage.closest("[data-product-wrapper]");
    var img = stage.querySelector("img");
    var json =
      wrapper &&
      wrapper.querySelector(
        '[type="application/json"][data-name="main-product"]',
      );
    if (!img || !json) return null;
    var variants = [];
    try {
      variants = JSON.parse(json.textContent || "[]");
    } catch (e) {
      return null;
    }
    function onChange(e) {
      if (!e.target || e.target.name !== "id") return;
      var v = variants.find(function (x) {
        return String(x.id) === String(e.target.value);
      });
      var src = v && v.featured_image && v.featured_image.src;
      if (!src) return;
      var url = src + (src.indexOf("?") > -1 ? "&" : "?") + "width=1200";
      if (img.getAttribute("src") === url) return;
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.src = url;
      img.alt = v.featured_image.alt || img.alt;
    }
    wrapper.addEventListener("change", onChange);
    return {
      destroy: function () {
        wrapper.removeEventListener("change", onChange);
      },
    };
  }

  /* ------------------------------------------------------------ */
  /* Boot                                                          */
  /* ------------------------------------------------------------ */

  var controllers = new Map();

  function boot(scope) {
    Array.prototype.forEach.call(
      scope.querySelectorAll("[data-ac-stage]"),
      function (el) {
        if (controllers.has(el)) return;
        var c = initStage(el);
        if (c) controllers.set(el, c);
      },
    );
    Array.prototype.forEach.call(
      scope.querySelectorAll("[data-ac-stage-single]"),
      function (el) {
        if (controllers.has(el)) return;
        var c = initSingle(el);
        if (c) controllers.set(el, c);
      },
    );
    Array.prototype.forEach.call(
      scope.querySelectorAll("[data-ac-quick]"),
      function (el) {
        if (controllers.has(el)) return;
        var c = initQuick(el);
        if (c) controllers.set(el, c);
      },
    );
    Array.prototype.forEach.call(
      scope.querySelectorAll("[data-ac-molecule]"),
      function (el) {
        if (controllers.has(el)) return;
        var c = initMolecule(el);
        if (c) controllers.set(el, c);
      },
    );
  }

  function teardown(scope) {
    controllers.forEach(function (c, el) {
      if (scope.contains(el)) {
        c.destroy();
        controllers.delete(el);
      }
    });
  }

  function start() {
    boot(document);
    window.__acPdp = { controllers: controllers };
  }

  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
  });
  document.addEventListener("shopify:section:unload", function (e) {
    teardown(e.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
